import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useSearchParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from '@/lib/auth';
import { PrivacyProvider } from '@/lib/privacy';
import { AppShell } from '@/layout/AppShell';
import { ApiError } from '@/lib/api';
import AuthPage from '@/routes/AuthPage';
import Landing from '@/routes/Landing';
import Overview from '@/routes/Overview';
import { CardSkeleton } from '@/components/ui';

// The heavier screens load on demand — Overview is what most sessions open on.
const Transactions = lazy(() => import('@/routes/Transactions'));
const Analytics = lazy(() => import('@/routes/Analytics'));
const Advisor = lazy(() => import('@/routes/Advisor'));
const Investments = lazy(() => import('@/routes/Investments'));
const Settings = lazy(() => import('@/routes/Settings'));
const Share = lazy(() => import('@/routes/Share'));
const Budgets = lazy(() => import('@/routes/Budgets'));
const Accounts = lazy(() => import('@/routes/Accounts'));
const Admin = lazy(() => import('@/routes/admin'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Auth failures and client mistakes won't fix themselves on a retry.
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
        return failureCount < 2;
      },
    },
  },
});

/** The signed-in app lives under this prefix; `/` is the public landing page. */
const APP = '/app';

/**
 * Paths the app answered on before it moved under `/app`.
 *
 * These stay forever. An iOS Shortcut someone pasted `…/share?text=` into, a
 * bookmark, a home-screen icon from an older install — all of them still land
 * somewhere useful, query string intact. The API was never told about these
 * paths, so nothing on that side needs to change.
 */
const MOVED = ['budgets', 'transactions', 'analytics', 'advisor', 'investments', 'accounts', 'settings', 'share'];

const bootSkeleton = (
  <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 24 }}>
    <div style={{ width: 'min(420px, 100%)' }}>
      <CardSkeleton lines={3} height={160} />
    </div>
  </div>
);

/** Forwards an old-style URL to its `/app` equivalent, keeping the query. */
function Moved() {
  const location = useLocation();
  return <Navigate to={`${APP}${location.pathname}${location.search}`} replace />;
}

function Root() {
  const location = useLocation();

  // A root URL carrying a query is a shortcut or share target (/?record=1),
  // not someone visiting the site — hand it to the app with the query intact.
  if (location.search) return <Navigate to={`${APP}${location.search}`} replace />;

  // The landing page is the front door for everyone. Being signed in doesn't
  // bounce you past it — it just swaps the sign-up calls to action for a way
  // into the app. Rendering straight away also keeps the marketing page off
  // the critical path of the cold-start session refresh.
  return <Landing />;
}

function Protected() {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Hold the render until the cold-start refresh settles, so a signed-in user
  // never sees the auth screen flash on reload.
  if (loading) return bootSkeleton;

  if (!user) {
    // Carry the destination through sign-in. Without this, a shortcut firing
    // at /app/share?text=coffee+4.50 on a lapsed session drops the text the
    // user just dictated and leaves them on an empty home screen.
    const next = `${location.pathname}${location.search}`;
    const target = next === APP ? '/signin' : `/signin?next=${encodeURIComponent(next)}`;
    return <Navigate to={target} replace />;
  }
  return <AppShell />;
}

function PublicOnly() {
  const { user, loading } = useAuth();
  const [params] = useSearchParams();

  if (loading) return null;
  if (user) {
    const next = params.get('next');
    // Only ever return to an in-app path — never an absolute URL, which would
    // turn the sign-in screen into an open redirect.
    const safe = next && next.startsWith('/') && !next.startsWith('//') ? next : APP;
    return <Navigate to={safe} replace />;
  }
  return <AuthPage />;
}

const fallback = (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
    <CardSkeleton lines={4} height={220} />
  </div>
);

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <PrivacyProvider>
            <Routes>
              <Route path="/" element={<Root />} />
              <Route path="/signin" element={<PublicOnly />} />
              <Route path={APP} element={<Protected />}>
                <Route index element={<Overview />} />
                <Route
                  path="transactions"
                  element={
                    <Suspense fallback={fallback}>
                      <Transactions />
                    </Suspense>
                  }
                />
                <Route path="budgets" element={<Suspense fallback={fallback}><Budgets /></Suspense>} />
                <Route
                  path="analytics"
                  element={
                    <Suspense fallback={fallback}>
                      <Analytics />
                    </Suspense>
                  }
                />
                <Route
                  path="advisor"
                  element={
                    <Suspense fallback={fallback}>
                      <Advisor />
                    </Suspense>
                  }
                />
                <Route
                  path="investments"
                  element={
                    <Suspense fallback={fallback}>
                      <Investments />
                    </Suspense>
                  }
                />
                <Route
                  path="accounts"
                  element={
                    <Suspense fallback={fallback}>
                      <Accounts />
                    </Suspense>
                  }
                />
                <Route
                  path="settings"
                  element={
                    <Suspense fallback={fallback}>
                      <Settings />
                    </Suspense>
                  }
                />
                {/* Nested wildcard: Admin owns its own <Routes> for its
                    subpages, and guards itself on user.role === 'ADMIN'. */}
                <Route
                  path="admin/*"
                  element={
                    <Suspense fallback={fallback}>
                      <Admin />
                    </Suspense>
                  }
                />
                {/* PWA share target. Sits behind the auth gate like everything
                    else, so an unauthenticated share lands on sign-in first. */}
                <Route
                  path="share"
                  element={
                    <Suspense fallback={fallback}>
                      <Share />
                    </Suspense>
                  }
                />
                {/* A mistyped in-app URL stays in the app rather than
                    dropping a signed-in user out onto the landing page. */}
                <Route path="*" element={<Navigate to={APP} replace />} />
              </Route>
              {MOVED.map((path) => (
                <Route key={path} path={`/${path}`} element={<Moved />} />
              ))}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </PrivacyProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
